/**
 * Coach Feedback Loop System
 *
 * Tracks coach judgment on AI-generated outputs to improve AI quality over time.
 * This module provides utilities to track when coaches accept, edit, or reject
 * AI outputs across different features.
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Types of AI outputs that can receive feedback
 */
export type FeedbackType =
  | 'transcript_analysis'
  | 'signal'
  | 'nudge'
  | 'graduation'
  | 'coach_veera';

/**
 * Actions coaches can take on AI outputs
 */
export type FeedbackAction =
  | 'accept'    // AI was correct, used as-is
  | 'edit'      // AI was close but needed refinement
  | 'remove'    // AI was wrong, removed entirely
  | 'dismiss'   // Signal/alert was overcalled
  | 'override'  // Disagreed with recommendation
  | 'act'       // Acted on signal/nudge
  | 'ignore';   // No action taken (implicit feedback)

/**
 * Feedback event data
 */
export interface FeedbackEvent {
  feedbackType: FeedbackType;
  sourceId?: string;
  sourceFunction?: string;
  aiOutput: unknown;
  aiConfidence?: number;
  action: FeedbackAction;
  coachOutput?: unknown;
  timeToActionMs?: number;
  context?: Record<string, unknown>;
  requireReadback?: boolean;
  feedbackId?: string;
}

/**
 * Track a coach feedback event
 *
 * @param coachId - The coach's user ID
 * @param leaderId - The leader's user ID (optional for global actions)
 * @param event - The feedback event data
 *
 * @example
 * ```ts
 * // Track transcript analysis approval
 * await trackCoachFeedback(coachId, leaderId, {
 *   feedbackType: 'transcript_analysis',
 *   sourceId: transcriptId,
 *   sourceFunction: 'analyze-transcript',
 *   aiOutput: originalAnalysis,
 *   action: hasEdits ? 'edit' : 'accept',
 *   coachOutput: hasEdits ? editedAnalysis : undefined,
 *   timeToActionMs: Date.now() - startTime,
 *   context: { behaviorId, phase },
 * });
 * ```
 */
export async function trackCoachFeedback(
  coachId: string,
  leaderId: string | undefined,
  event: FeedbackEvent
): Promise<{ success: boolean; error?: string; feedbackId?: string }> {
  try {
    const feedbackId = event.requireReadback ? (event.feedbackId || crypto.randomUUID()) : undefined;
    const payload = {
      ...(feedbackId ? { id: feedbackId } : {}),
      coach_id: coachId,
      leader_id: leaderId || null,
      feedback_type: event.feedbackType,
      source_id: event.sourceId || null,
      source_function: event.sourceFunction || null,
      ai_output: event.aiOutput,
      ai_confidence: event.aiConfidence || null,
      action: event.action,
      coach_output: event.coachOutput || null,
      time_to_action_ms: event.timeToActionMs || null,
      context: event.context || null,
    };
    const { error } = await supabase.from('coach_feedback').insert(payload);

    const duplicateRetry = Boolean(feedbackId && event.requireReadback && error?.code === '23505');
    if (error && !duplicateRetry) {
      // Log but don't fail the main action
      console.error('[coachFeedback] Failed to track feedback:', error);
      return { success: false, error: error.message };
    }

    if (feedbackId) {
      const readbackBase = supabase.from('coach_feedback')
        .select('id, coach_id, leader_id, feedback_type, source_id, source_function, action')
        .eq('id', feedbackId)
        .eq('coach_id', coachId);
      const readbackQuery = leaderId
        ? readbackBase.eq('leader_id', leaderId)
        : readbackBase.is('leader_id', null);
      const readback = await readbackQuery.maybeSingle();
      if (readback.error) {
        return { success: false, error: readback.error.message || 'Feedback readback failed' };
      }
      const row = readback.data;
      if (
        !row
        || row.id !== feedbackId
        || row.feedback_type !== event.feedbackType
        || row.source_id !== (event.sourceId || null)
        || row.source_function !== (event.sourceFunction || null)
        || row.action !== event.action
      ) {
        return { success: false, error: 'Feedback readback did not match the saved action.' };
      }
    }

    return { success: true, feedbackId };
  } catch (err) {
    // Never fail the main action if feedback tracking fails
    console.error('[coachFeedback] Exception tracking feedback:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Create a timer for tracking review time
 *
 * @returns Object with methods to start and stop timer
 *
 * @example
 * ```ts
 * const timer = createReviewTimer();
 * timer.start();
 * // ... user reviews content ...
 * const elapsed = timer.stop();
 * ```
 */
export function createReviewTimer(): {
  start: () => void;
  stop: () => number;
  getElapsed: () => number;
} {
  let startTime: number | null = null;

  return {
    start: () => {
      startTime = Date.now();
    },
    stop: () => {
      if (startTime === null) return 0;
      return Date.now() - startTime;
    },
    getElapsed: () => {
      if (startTime === null) return 0;
      return Date.now() - startTime;
    },
  };
}

/**
 * Track transcript analysis feedback
 */
export async function trackTranscriptAnalysisFeedback(
  coachId: string,
  leaderId: string,
  transcriptId: string,
  originalAnalysis: unknown,
  action: 'accept' | 'edit' | 'remove',
  options?: {
    editedAnalysis?: unknown;
    timeToActionMs?: number;
    behaviorId?: string;
    phase?: string;
  }
): Promise<void> {
  await trackCoachFeedback(coachId, leaderId, {
    feedbackType: 'transcript_analysis',
    sourceId: transcriptId,
    sourceFunction: 'analyze-transcript',
    aiOutput: originalAnalysis,
    action,
    coachOutput: options?.editedAnalysis,
    timeToActionMs: options?.timeToActionMs,
    context: {
      behaviorId: options?.behaviorId,
      phase: options?.phase,
    },
  });
}

/**
 * Track signal interaction feedback
 */
export async function trackSignalFeedback(
  coachId: string,
  leaderId: string,
  signalId: string,
  signal: { type: string; priority: string; summary: string },
  action: 'act' | 'dismiss' | 'ignore'
): Promise<void> {
  await trackCoachFeedback(coachId, leaderId, {
    feedbackType: 'signal',
    sourceId: signalId,
    sourceFunction: 'generate-coach-signals',
    aiOutput: signal,
    action,
    context: {
      displayedPriority: signal.priority,
      signalType: signal.type,
    },
  });
}

/**
 * Track nudge decision feedback
 */
export async function trackNudgeFeedback(
  coachId: string,
  leaderId: string,
  nudgeId: string,
  originalContent: string,
  action: 'accept' | 'edit' | 'remove',
  options?: {
    editedContent?: string;
    behaviorId?: string;
  }
): Promise<void> {
  await trackCoachFeedback(coachId, leaderId, {
    feedbackType: 'nudge',
    sourceId: nudgeId,
    sourceFunction: 'generate-nudge',
    aiOutput: { content: originalContent },
    action,
    coachOutput: options?.editedContent
      ? { content: options.editedContent }
      : undefined,
    context: {
      behaviorId: options?.behaviorId,
    },
  });
}

/**
 * Track graduation recommendation feedback
 */
export async function trackGraduationFeedback(
  coachId: string,
  leaderId: string,
  leaderBehaviorId: string,
  recommendation: { readinessLevel: string; reasonSummary: string },
  action: 'accept' | 'override',
  options?: {
    coachDecision?: string;
    behaviorId?: string;
  }
): Promise<void> {
  await trackCoachFeedback(coachId, leaderId, {
    feedbackType: 'graduation',
    sourceId: leaderBehaviorId,
    sourceFunction: 'analyze-behavior-readiness',
    aiOutput: recommendation,
    action,
    coachOutput: options?.coachDecision
      ? { decision: options.coachDecision }
      : undefined,
    context: {
      behaviorId: options?.behaviorId,
      recommendedAction:
        recommendation.readinessLevel === 'high' ? 'graduate' : 'continue',
    },
  });
}
